import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";
import {
  useNavigate
} from "react-router-dom";
import { db } from "../../firebase";
import {
  createPurchaseOrderFromRequest, calculateLandedCost, fetchRecentPOHistory, getItemRateHistory
} from "../../pages/services/purchase/purchaseOrderService";
import QuickCreateVendor  from "../quickcreate/forms/QuickCreateVendor";   // ✅ NEW
import QuickCreateModal   from "../quickcreate/QuickCreateModal";           // ✅ NEW
// D-291 P3c: each line carries the rate BEFORE GST, GST % (required, from
// Item Master — never a silent 18) and HSN the way the vendor will bill it;
// "Rate not agreed yet" per line; an Unregistered vendor's lines are GST 0.
// The ordered value comes from computeOrderValue(); the service re-checks
// every line (checkPOLines) and re-reads the vendor before it saves.
import { computeOrderValue, supplyTypeOf, vendorGstinOf, vendorGstStatus, checkHsn } from "../../utils/gstBill";
import { checkPOLines } from "../../utils/poLines";
import { hasUnit } from "../../utils/units";
import { activeMasterFor } from "../../utils/itemMatch";
import UnitPicker from "./UnitPicker";
import { useCompany } from "../../context/CompanyContext";

// Item Master gst_percent is a number or null (not set) → the box value.
const gstBoxValue = (gst) => (gst === null || gst === undefined || gst === "" ? "" : String(gst));
const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function VendorAssignmentModal({

  

  open,
  onClose,
  purchaseRequest

}) {

  const [vendors, setVendors] = useState([]);
    const navigate =
    useNavigate();
  const { company } = useCompany();

  const [vendorId, setVendorId] = useState("");

  const [deliveryDate, setDeliveryDate] = useState("");

  const [priority, setPriority] = useState("normal");

  const [transport, setTransport] = useState("");

  const [remarks, setRemarks] = useState("");

  const [loadingVendors,  setLoadingVendors]  = useState(false);
  const [showVendorQuick, setShowVendorQuick] = useState(false); // ✅ NEW
  // D-304 B: one PO per click — the button stays disabled while saving
  // (a double click used to create two POs from one PR).
  const [creating,        setCreating]        = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (open && purchaseRequest?.vendor_id) {
      setVendorId(purchaseRequest.vendor_id);
    }
  }, [open, purchaseRequest, vendors]); // vendors dependency add karo

  // Merged items — same item_id ki qty add ho jaaye.
  // D-304 B: merged ONLY by item_id — never by name (two different items can
  // share a name). A line without an item_id (old custom PR line) stays its
  // own row. `_key` keys this screen's rate inputs; MRF sources are kept.
  const mergedItems = React.useMemo(() => {
    const map = {};
    (purchaseRequest?.items || []).forEach((item, index) => {
      const itemId = String(item.item_id || "").trim();
      const key    = itemId || `line_${index}`;
      if (map[key]) {
        map[key].qty += Number(item.qty || 0);
        map[key].sources = [...(map[key].sources || []), ...(item.sources || [])];
      } else {
        map[key] = {
          ...item,
          _key:    key,
          qty:     Number(item.qty || 0),
          sources: Array.isArray(item.sources) ? item.sources : [],
        };
      }
    });
    return Object.values(map);
  }, [purchaseRequest]);

  // ── Rate per line (D-291 P3c) ────────────────────────────────────────────
  // "Rate before GST" = the rate the vendor quoted; it is saved as the PO
  // line's base_rate (and rate, until the GRN writes the stock rate). A line
  // without a rate must be ticked "Rate not agreed yet" (rate comes on the
  // bill). GST % and HSN are pre-filled from Item Master when it has them.
  const [itemRateInputs, setItemRateInputs] = useState({}); // { [_key]: { quoted_rate, discount_type, discount_value, gst_percent, hsn_code, rate_not_agreed } }
  const [freightAmount,  setFreightAmount]  = useState("");

  // Item Master records — the source of GST %, HSN, item code and category.
  // Own company + legacy company_id "" items, the same two queries the GRN
  // screen uses. A line finds its item through activeMasterFor() (P4-8).
  const [masterItems, setMasterItems] = useState([]);

  // Lowest-Rate Suggestion — same source (purchase_orders history) and
  // same reference-only display as CreateDirectPO.js. Fetched once when
  // the modal opens, matched per-item client-side.
  const [poHistory,        setPoHistory]        = useState([]);
  const [openHistoryKey,   setOpenHistoryKey]    = useState(null);

  useEffect(() => {
    if (!open) return;
    const defaults = {};
    mergedItems.forEach(item => {
      // GST % starts EMPTY — never a silent "18" (D-291 P3c). Filled from
      // Item Master below when the item has a GST %.
      defaults[item._key] = {
        quoted_rate: "", discount_type: "percent", discount_value: "",
        gst_percent: "", hsn_code: "", rate_not_agreed: false,
        unit: "", // P3c-7: picked here only when the PR line and Item Master have no unit
      };
    });
    setItemRateInputs(defaults);
    setFreightAmount("");
    setOpenHistoryKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, purchaseRequest]);

  useEffect(() => {
    if (!open) return;
    const cid = purchaseRequest?.company_id || "";
    if (!cid) return;
    const loadItems = async () => {
      try {
        const own = await getDocs(query(collection(db, "items"), where("company_id", "==", cid)));
        let docs = own.docs;
        try {
          const legacy = await getDocs(query(collection(db, "items"), where("company_id", "==", "")));
          docs = [...docs, ...legacy.docs];
        } catch (e) {
          console.warn("[VendorAssignmentModal] legacy items not readable (non-blocking):", e);
        }
        setMasterItems(docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("[VendorAssignmentModal] items load failed:", e);
      }
    };
    loadItems();
  }, [open, purchaseRequest?.company_id]);

  // P4-8: active items only, by item_id (old PR lines may carry the doc id).
  // A disabled item never pre-fills; two active items on one id fill nothing.
  const masterOf = (item) => activeMasterFor(masterItems, item);

  // P3c-7: the line's unit as already known - PR line first, then Item
  // Master. "" -> nobody knows it -> the user picks it in the Qty column.
  const knownUnitOf = (item) => [item?.unit, item?.uom, masterOf(item)?.unit].find(hasUnit) || "";

  // When Item Master arrives, fill GST % / HSN on lines the user has not
  // typed yet (an item without GST % leaves the box empty).
  useEffect(() => {
    if (!open || masterItems.length === 0) return;
    setItemRateInputs(prev => {
      const next = { ...prev };
      mergedItems.forEach(item => {
        const m = masterOf(item);
        const cur = next[item._key];
        if (!m || !cur) return;
        next[item._key] = {
          ...cur,
          gst_percent: cur.gst_percent === "" ? gstBoxValue(m.gst_percent) : cur.gst_percent,
          hsn_code:    cur.hsn_code    === "" ? (m.hsn_code || "")        : cur.hsn_code,
        };
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterItems, open, purchaseRequest]);

  useEffect(() => {
    if (!open) return;
    const loadHistory = async () => {
      const history = await fetchRecentPOHistory(purchaseRequest?.company_id || "");
      setPoHistory(history);
    };
    loadHistory();
  }, [open, purchaseRequest?.company_id]);

  const updateItemRateInput = (key, field, value) => {
    setItemRateInputs(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  // ── Vendor GST status (D-291 P3c) — Unregistered vendor → every line GST 0.
  const selectedVendor     = vendors.find(v => v.id === vendorId) || null;
  const vendorStatus       = selectedVendor ? vendorGstStatus(selectedVendor) : "not_entered";
  const vendorUnregistered = vendorStatus === "unregistered";
  const effectiveGst       = (inputs) => (vendorUnregistered ? 0 : (inputs?.gst_percent ?? ""));
  const supplyType = selectedVendor ? supplyTypeOf(company?.gst_number, vendorGstinOf(selectedVendor)) : null;

  // Ordered value — the same computeOrderValue() the PO page and approval use.
  const orderValue = computeOrderValue(
    mergedItems.map(item => {
      const inputs = itemRateInputs[item._key] || {};
      return {
        line_id: item._key, item_name: item.item_name, ordered_qty: item.qty,
        base_rate: inputs.quoted_rate, rate_not_agreed: inputs.rate_not_agreed === true,
        discount_type: inputs.discount_type, discount_value: inputs.discount_value,
        gst_percent: effectiveGst(inputs),
      };
    }),
    supplyType
  );

  // Same calculateLandedCost() the backend uses at creation — what's shown
  // here is guaranteed identical to what gets stored.
  const landedItems = calculateLandedCost(
    mergedItems.map(item => {
      const inputs = itemRateInputs[item._key] || {};
      return {
        ...item,
        qty:            Number(item.qty || 0),
        base_rate:      inputs.rate_not_agreed ? 0 : Number(inputs.quoted_rate || 0),
        discount_type:  inputs.discount_type || "percent",
        discount_value: Number(inputs.discount_value || 0),
        gst_percent:    Number(effectiveGst(inputs) || 0),
      };
    }),
    Number(freightAmount || 0)
  );
  const landedGrandTotal = landedItems.reduce((sum, item) => sum + (item.landed_total || 0), 0);

  

  const fetchVendors = async () => {

    try {

      setLoadingVendors(true);

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const snap = await getDocs(
        query(
          collection(db, "vendors"),
          where("company_id", "==", user.company_id || "")
        )
      );

      const list = snap.docs.map((doc) => ({

        id: doc.id,

        ...doc.data()

      }));

      // Hide deactivated vendors (Vendor Master writes `status`, Quick Create `is_active`).
      setVendors(list.filter(v => v.status !== "inactive" && v.is_active !== false));

    } catch (error) {

      console.error(
        "Vendor fetch failed",
        error
      );

    } finally {

      setLoadingVendors(false);

    }
  };

  const handleClose = () => {

    setVendorId("");

    setDeliveryDate("");

    setPriority("normal");

    setTransport("");

    setRemarks("");

    onClose();
  };

  if (!open) return null;

  const handleCreatePO = async () => {

    if (creating) return;

    const selectedVendor = vendors.find(
      (v) => v.id === vendorId
    );

    // VALIDATIONS

    if (!selectedVendor) {
      alert("Please select vendor");
      return;
    }

    if (!deliveryDate) {
      alert("Please select delivery date");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    // D-291 P3c: the boxes go to the service as typed — an empty rate or
    // GST box is never turned into 0 here. Code / category / unit come from
    // Item Master when the PR line has none. checkPOLines() is the same check
    // the service runs again before it saves.
    const payloadItems = mergedItems.map(({ _key, ...item }) => {
      const inputs = itemRateInputs[_key] || {};
      const m = masterOf(item);
      return {
        ...item,
        item_code:       item.item_code || m?.item_code || "",
        item_category:   item.item_category || m?.item_category || m?.category || "",
        unit:            knownUnitOf(item) || inputs.unit || "",
        // `rate` cleared on purpose: an old PR line's own `rate` must never
        // stand in for a rate nobody typed here.
        rate:            "",
        base_rate:       inputs.rate_not_agreed ? "" : (inputs.quoted_rate ?? ""),
        rate_not_agreed: inputs.rate_not_agreed === true,
        discount_type:   inputs.discount_type || "percent",
        discount_value:  inputs.discount_value ?? "",
        gst_percent:     effectiveGst(inputs),
        hsn_code:        inputs.hsn_code || "",
      };
    });
    const lineCheck = checkPOLines(payloadItems, { vendorUnregistered });
    if (!lineCheck.ok) {
      alert(`Please fix these lines:\n- ${lineCheck.errors.join("\n- ")}`);
      return;
    }

    const payload = {
      company_id:                    purchaseRequest?.company_id || "",
      vendor_id:                     selectedVendor.id || "",
      vendor_name:                   selectedVendor.vendor_name || selectedVendor.name || "",
      delivery_date:                 deliveryDate,
      priority,
      transport,
      remarks,
      source_purchase_request_id:    purchaseRequest?.id || "",
      freight_amount:                Number(freightAmount || 0),
      items:                         payloadItems,  // rate before GST + GST % + HSN per line (D-291 P3c)
      created_by:                    user.uid || user.id || "",
      created_by_name:               user.name || user.email || "",
    };

    setCreating(true);
    try {

      // ONE transaction: PO created + PR marked converted — refused if this
      // PR already has a PO (second tab / double click) or was cancelled.
      const response =
        await createPurchaseOrderFromRequest(payload);

      alert(
        `PO Created Successfully

PO: ${response.po_number}

Vendor Assigned Successfully`
      );

      handleClose();

      navigate(
        "/purchase-orders",
        {
          replace: true,
          state: {
            highlight_po_id: response.po_id,
            po_created:      true,
            po_number:       response.po_number
          }
        }
      );

    } catch (error) {

      console.error(
        "PO creation failed",
        error
      );

      alert(error.message || "Failed to create purchase order");

    } finally {

      setCreating(false);
    }
  };

  return (

    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">

      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-[fadeIn_.2s_ease]">

        {/* HEADER */}

        <div className="flex items-center justify-between px-6 py-5 border-b bg-gradient-to-r from-slate-50 to-white">

          <div>

            <h2 className="text-2xl font-black text-slate-800">
              Assign Vendor
            </h2>

            <p className="text-sm text-slate-500 mt-1">
              Create purchase order from procurement request
            </p>

          </div>

          <button
            onClick={handleClose}
            className="text-sm px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition"
          >
            Close
          </button>

        </div>

        {/* BODY */}

        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">

          {/* PURCHASE INFO */}

          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">

            <div className="text-sm text-slate-500">
              Purchase Request
            </div>

            <div className="text-lg font-bold text-slate-800 mt-1">
              {purchaseRequest?.items?.length || 0} Items
            </div>

          </div>

          {/* VENDOR */}

          <div>

            <label className="text-sm font-semibold text-slate-700 block mb-2">
              Vendor
            </label>

            {/* ✅ Dropdown + Quick Add button */}
            <div className="flex gap-2 items-center">
              <select
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="flex-1 border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">
                  {loadingVendors ? "Loading Vendors..." : "Select Vendor"}
                </option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.vendor_name || vendor.name || "Unnamed Vendor"}
                  </option>
                ))}
              </select>

              {/* ✅ + button */}
              <button
                type="button"
                onClick={() => setShowVendorQuick(true)}
                title="Add new vendor"
                className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border border-dashed border-indigo-300 text-indigo-500 bg-indigo-50 hover:bg-indigo-100 cursor-pointer transition"
              >
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                  <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* ✅ QuickCreate Vendor Modal */}
            <QuickCreateModal
              open={showVendorQuick}
              onClose={() => setShowVendorQuick(false)}
              title="Add New Vendor"
            >
              <QuickCreateVendor
                companyId={
                  JSON.parse(localStorage.getItem("user") || "{}").company_id || ""
                }
                onCreated={(newVendor) => {
                  // ✅ vendors list mein add karo + auto-select
                  setVendors(prev => [...prev, newVendor]);
                  setVendorId(newVendor.id);
                  setShowVendorQuick(false);
                }}
                onCancel={() => setShowVendorQuick(false)}
              />
            </QuickCreateModal>

            {selectedVendor && (
              <div className="mt-2 text-xs">
                {vendorStatus === "registered" && (
                  <p className="text-slate-500">
                    GSTIN: {vendorGstinOf(selectedVendor)}
                    {supplyType === "intra" && " · same state → CGST + SGST"}
                    {supplyType === "inter" && " · other state → IGST"}
                  </p>
                )}
                {vendorStatus === "unregistered" && (
                  <p className="text-amber-700 font-medium">Unregistered vendor — no GST on this PO (all lines GST 0).</p>
                )}
                {vendorStatus === "not_entered" && (
                  <p className="text-amber-700">GSTIN not entered — add it in Vendor Master if the vendor is GST registered.</p>
                )}
                {vendorStatus === "invalid" && (
                  <p className="text-red-600">GSTIN on file is not valid ({vendorGstinOf(selectedVendor)}) — correct it in Vendor Master.</p>
                )}
              </div>
            )}

          </div>

          {/* DELIVERY DATE */}

          <div>

            <label className="text-sm font-semibold text-slate-700 block mb-2">
              Delivery Date
            </label>

            <input
              type="date"
              value={deliveryDate}
              onChange={(e) =>
                setDeliveryDate(e.target.value)
              }
              className="w-full border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-500"
            />

          </div>

          {/* PRIORITY */}

          <div>

            <label className="text-sm font-semibold text-slate-700 block mb-2">
              Priority
            </label>

            <select
              value={priority}
              onChange={(e) =>
                setPriority(e.target.value)
              }
              className="w-full border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-500"
            >

              <option value="normal">
                Normal
              </option>

              <option value="urgent">
                Urgent
              </option>

              <option value="critical">
                Critical
              </option>

            </select>

          </div>

          {/* TRANSPORT */}

          <div>

            <label className="text-sm font-semibold text-slate-700 block mb-2">
              Transport
            </label>

            <input
              type="text"
              value={transport}
              onChange={(e) =>
                setTransport(e.target.value)
              }
              placeholder="Transport details"
              className="w-full border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-500"
            />

          </div>

          {/* REMARKS */}

          <div>

            <label className="text-sm font-semibold text-slate-700 block mb-2">
              Remarks
            </label>

            <textarea
              rows={4}
              value={remarks}
              onChange={(e) =>
                setRemarks(e.target.value)
              }
              placeholder="Additional procurement notes..."
              className="w-full border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-500"
            />

          </div>

          {/* REQUEST ITEMS + RATE BREAKDOWN (reference only) */}

          <div>

            <div className="text-sm font-semibold text-slate-700 mb-1">
              Items & Rates
            </div>
            <p className="text-xs text-slate-400 mb-3">
              Rate before GST as the vendor quoted it, and GST % as the vendor will
              bill it (GST % comes from Item Master when it is set there). If the rate
              is not agreed yet, tick "Rate not agreed yet" - the rate comes on the bill,
              and GST % can stay blank until then.
            </p>

            <div className="border rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
                      <th className="text-left px-4 py-2 min-w-[240px]">Item</th>
                      <th className="text-right px-3 py-2 w-24">Qty</th>
                      <th className="text-right px-3 py-2 w-28">Rate before GST (₹)</th>
                      <th className="text-center px-3 py-2 w-36">Discount</th>
                      <th className="text-right px-3 py-2 w-20">GST % *</th>
                      <th className="text-left px-3 py-2 w-28">HSN</th>
                      <th className="text-right px-3 py-2 w-28">Landed Rate</th>
                      <th className="text-right px-4 py-2 w-32">Landed Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {mergedItems.map((item, index) => {
                      const key    = item._key;
                      const inputs = itemRateInputs[key] || {};
                      const landed = landedItems[index] || {};
                      return (
                        <tr key={`${key}_${index}`} className="bg-white hover:bg-slate-50 transition align-top">
                          <td className="px-4 py-2.5">
                            <div className="text-sm font-semibold text-slate-700">{item.item_name}</div>
                            <div className="text-xs text-slate-400">
                              {item.item_code || "-"}
                              {item.specification ? ` · ${item.specification}` : ""}
                            </div>
                            {(item.sources || []).length > 0 && (
                              <div className="text-[11px] text-slate-400 mt-0.5">
                                For: {item.sources.map(s => `${s.site_name || s.mrf_no || "MRF"} (${s.qty})`).join(", ")}
                              </div>
                            )}
                            {/* Lowest-Rate Suggestion — reference only, never
                                auto-fills Quoted Rate/Discount/GST above. */}
                            {(() => {
                              const history = getItemRateHistory(poHistory, item);
                              if (history.length === 0) return null;
                              const isOpen = openHistoryKey === key;
                              return (
                                <div className="mt-1">
                                  <button type="button"
                                    onClick={() => setOpenHistoryKey(isOpen ? null : key)}
                                    className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap">
                                    📊 {history.length} past rate{history.length > 1 ? "s" : ""} — from ₹{history[0].landed_rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                                  </button>
                                  {isOpen && (
                                    <div className="mt-1 border border-slate-200 rounded-lg bg-slate-50 p-2 space-y-1 min-w-[220px]">
                                      {history.map((h, i) => (
                                        <div key={i} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${i === 0 ? "bg-teal-50 font-semibold text-teal-800" : "text-slate-600"}`}>
                                          <span className="truncate">
                                            {h.vendor_name} · {h.created_at?.toDate ? h.created_at.toDate().toLocaleDateString("en-IN") : "—"}
                                            {i === 0 && <span className="ml-1 text-teal-600">(lowest)</span>}
                                          </span>
                                          <span className="whitespace-nowrap ml-2">₹{h.landed_rate.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-sm font-bold text-slate-800 whitespace-nowrap">
                            {/* P3c-7: no silent "nos" - a line without a unit picks one here. */}
                            {Number(item.qty || 0)} {knownUnitOf(item)}
                            {!knownUnitOf(item) && (
                              <div className="mt-1 min-w-[120px] text-left font-normal whitespace-normal">
                                <UnitPicker value={inputs.unit || ""} masterNoUnit={!!masterOf(item)}
                                  onChange={(u) => updateItemRateInput(key, "unit", u)} />
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <input type="number" min="0" step="0.01"
                              value={inputs.rate_not_agreed ? "" : (inputs.quoted_rate || "")}
                              onChange={e => updateItemRateInput(key, "quoted_rate", e.target.value)}
                              disabled={inputs.rate_not_agreed === true}
                              placeholder={inputs.rate_not_agreed ? "Not agreed" : "0"}
                              className="w-full min-w-[100px] px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-cyan-400 disabled:bg-slate-100" />
                            <label className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500 whitespace-nowrap cursor-pointer">
                              <input type="checkbox" checked={inputs.rate_not_agreed === true}
                                onChange={e => updateItemRateInput(key, "rate_not_agreed", e.target.checked)} />
                              Rate not agreed yet
                            </label>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex gap-1">
                              <select
                                value={inputs.discount_type || "percent"}
                                onChange={e => updateItemRateInput(key, "discount_type", e.target.value)}
                                className="px-1.5 py-1.5 border border-slate-300 rounded-lg text-xs outline-none bg-white shrink-0">
                                <option value="percent">%</option>
                                <option value="flat">₹</option>
                              </select>
                              {/* Width follows the mode — % tops out at 100
                                  (3 digits), flat ₹ can run into 6 digits
                                  (subtotal-sized). Same reasoning as
                                  CreateDirectPO.js's Discount column. */}
                              <input type="number" min="0"
                                max={inputs.discount_type === "flat" ? undefined : 100}
                                step="0.01" value={inputs.discount_value || ""}
                                onChange={e => updateItemRateInput(key, "discount_value", e.target.value)}
                                placeholder="0"
                                className={`px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-cyan-400 ${
                                  inputs.discount_type === "flat" ? "w-[110px]" : "w-[70px]"
                                }`} />
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <input type="number" min="0" max="40" step="0.01"
                              value={vendorUnregistered ? "0" : (inputs.gst_percent ?? "")}
                              onChange={e => updateItemRateInput(key, "gst_percent", e.target.value)}
                              disabled={vendorUnregistered}
                              placeholder={inputs.rate_not_agreed ? "On bill" : "GST"}
                              title={vendorUnregistered
                                ? "Unregistered vendor — no GST"
                                : inputs.rate_not_agreed
                                ? "Leave blank if the vendor's bill will decide GST. Type 0 only if this item truly has no GST."
                                : "GST % as the vendor will bill it"}
                              className={`w-full min-w-[70px] px-2 py-1.5 border rounded-lg text-sm text-right outline-none focus:ring-2 focus:ring-cyan-400 disabled:bg-slate-100 ${
                                !vendorUnregistered && !inputs.rate_not_agreed && String(inputs.gst_percent ?? "").trim() === "" ? "border-amber-400" : "border-slate-300"
                              }`} />
                          </td>
                          <td className="px-3 py-2.5">
                            {(() => {
                              const hsnCheck = checkHsn(inputs.hsn_code);
                              return (
                                <>
                                  <input type="text" inputMode="numeric" maxLength={8} value={inputs.hsn_code || ""}
                                    onChange={e => updateItemRateInput(key, "hsn_code", e.target.value.replace(/\s+/g, ""))}
                                    placeholder="Optional"
                                    className={`w-full min-w-[90px] px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-cyan-400 ${
                                      hsnCheck.ok ? "border-slate-300" : "border-red-400"
                                    }`} />
                                  {!hsnCheck.ok && <p className="text-[11px] text-red-600 mt-0.5">{hsnCheck.reason}</p>}
                                </>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2.5 text-right text-sm font-semibold text-teal-700 whitespace-nowrap">
                            ₹{(landed.landed_rate || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2.5 text-right text-sm font-semibold text-teal-700 whitespace-nowrap">
                            ₹{(landed.landed_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* ✅ Grand Total GRN pe hoga — PO pe nahi */}
            </div>

          </div>

          {/* FREIGHT (one flat amount for the whole PO) — reference only */}
          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-2">
              Freight / Transport (₹, whole PO) — optional
            </label>
            <div className="flex items-end gap-4 flex-wrap">
              <input type="number" min="0" step="0.01" value={freightAmount}
                onChange={e => setFreightAmount(e.target.value)}
                placeholder="0"
                className="w-48 border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-500" />
              <div>
                <p className="text-xs text-slate-400">Total Landed Cost (all items, ref. only)</p>
                <p className="text-lg font-bold text-teal-700">
                  ₹{landedGrandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Split across items proportionally — reference only, never changes the GRN rate.
            </p>
          </div>

          {/* ORDER VALUE (D-291 P3c) — what the approver will see: taxable +
              GST = total. Freight above is reference only and not in it. */}
          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 text-sm">
            <div className="text-slate-500">Order Value</div>
            <div className="mt-1 text-slate-700 space-y-0.5">
              <div>Taxable: <span className="font-semibold">₹{inr(orderValue.taxable_total)}</span></div>
              {orderValue.supply_type === "intra" && (
                <div>CGST ₹{inr(orderValue.cgst)} + SGST ₹{inr(orderValue.sgst)}</div>
              )}
              {orderValue.supply_type === "inter" && <div>IGST ₹{inr(orderValue.igst)}</div>}
              {!orderValue.supply_type && (
                <div>GST ₹{inr(orderValue.tax_total)} <span className="text-xs text-slate-400">(CGST / SGST or IGST is decided on the bill)</span></div>
              )}
            </div>
            <div className="text-xl font-black text-slate-800 mt-1">₹{inr(orderValue.total)}</div>
            {orderValue.rate_not_agreed.length > 0 && (
              <div className="text-xs text-amber-600 mt-1">
                {orderValue.rate_not_agreed.length} line{orderValue.rate_not_agreed.length > 1 ? "s" : ""} with rate not agreed — not in this value.
              </div>
            )}
            {(orderValue.rate_missing.length > 0 || orderValue.errors.length > 0) && (
              <div className="text-xs text-red-600 mt-1">Some lines are not complete yet (rate or GST %) — they are not in this value.</div>
            )}
          </div>

        </div>

        {/* FOOTER */}

        <div className="flex justify-end gap-3 px-6 py-5 border-t bg-slate-50">

          <button
            onClick={handleClose}
            className="px-5 py-3 rounded-2xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition"
          >
            Cancel
          </button>

          <button
  onClick={handleCreatePO}
  disabled={
    creating ||
    !vendorId ||
    !deliveryDate
  }
            className={`px-6 py-3 rounded-2xl text-white font-semibold transition ${
              creating || !vendorId || !deliveryDate
                ? "bg-slate-300 cursor-not-allowed"
                : "bg-gradient-to-r from-indigo-600 to-cyan-500 hover:scale-[1.02]"
            }`}
          >
            {creating ? "Creating PO..." : "Create PO"}
          </button>

        </div>

      </div>

    </div>

  );
}